import apiClient from './apiClient';

const apiBaseURL = '/api/indexing';

export interface IndexDocumentResponse {
  document_id: number;
  status: string;
  message: string;
}

export interface IndexingStatusResponse {
  document_id: number;
  status: string;
  chunks_count: number;
  neo4j_database?: string;
  indexed_at?: string;
  error_message?: string;
  created_at?: string;
  updated_at?: string;
}

export interface SearchEmbeddingsResult {
  chunk_id: string;
  text: string;
  document_id: string;
  document_name: string;
  score: number;
  chunk_index: number;
}

export interface SearchEmbeddingsResponse {
  query: string;
  results: SearchEmbeddingsResult[];
  total: number;
}

export interface IndexContextResponse {
  message: string;
  context_type: string;
  context_id: string;
  document_count: number;
  document_ids: number[];
}

export interface IndexingTaskStatus {
  document_id: number;
  document_name: string;
  status: 'indexing' | 'indexed' | 'failed' | 'not_indexed';
  chunks_count: number;
  error_message?: string;
  indexed_at?: string;
  updated_at?: string;
}

export interface IndexingTasksResponse {
  tasks: IndexingTaskStatus[];
  total: number;
  indexing_count: number;
  indexed_count: number;
  failed_count: number;
}

export interface RetryFailedResponse {
  message: string;
  retried_count: number;
  document_ids: number[];
}

export interface ContextFilter {
  type: 'domain' | 'use_case' | 'cadrage' | 'custom_folder' | 'project';
  id: string;
}

/**
 * Service for document indexing and embedding search operations
 */
export const indexingService = {
  /**
   * Index a document in Neo4j
   * @param documentId - Document ID to index
   * @param forceReindex - Force reindexing even if already indexed
   */
  async indexDocument(documentId: string, forceReindex = false): Promise<IndexDocumentResponse> {
    const response = await apiClient.post<IndexDocumentResponse>(
      `${apiBaseURL}/documents/${documentId}`,
      {},
      { params: { force_reindex: forceReindex } }
    );
    return response.data;
  },

  /**
   * Get indexing status of a document
   * @param documentId - Document ID
   */
  async getIndexingStatus(documentId: string): Promise<IndexingStatusResponse> {
    const response = await apiClient.get<IndexingStatusResponse>(
      `${apiBaseURL}/documents/${documentId}/status`
    );
    return response.data;
  },

  /**
   * Search in document embeddings
   * @param query - Search query
   * @param contextFilters - Optional filters by domain/use_case/cadrage
   */
  async searchEmbeddings(
    query: string,
    contextFilters?: ContextFilter[]
  ): Promise<SearchEmbeddingsResponse> {
    const response = await apiClient.post<SearchEmbeddingsResponse>(`${apiBaseURL}/search`, {
      query,
      context_filters: contextFilters,
    });
    return response.data;
  },

  /**
   * Batch index multiple documents
   * @param documentIds - Array of document IDs
   * @param forceReindex - Force reindexing even if already indexed
   */
  async batchIndexDocuments(documentIds: number[], forceReindex = false): Promise<any> {
    const response = await apiClient.post(
      `${apiBaseURL}/batch`,
      { document_ids: documentIds },
      { params: { force_reindex: forceReindex } }
    );
    return response.data;
  },

  /**
   * Get indexing tasks with their status
   * @param limit - Maximum number of tasks to return
   * @param statusFilter - Optional status filter
   */
  async getIndexingTasks(
    limit = 50,
    statusFilter?: 'indexing' | 'indexed' | 'failed' | 'not_indexed'
  ): Promise<IndexingTasksResponse> {
    const response = await apiClient.get<IndexingTasksResponse>(`${apiBaseURL}/tasks`, {
      params: {
        limit,
        status_filter: statusFilter,
      },
    });
    return response.data;
  },

  /**
   * Retry all failed indexing tasks
   */
  async retryFailedDocuments(): Promise<RetryFailedResponse> {
    const response = await apiClient.post<RetryFailedResponse>(`${apiBaseURL}/retry-failed`);
    return response.data;
  },

  /**
   * Index all documents in a context (domain, use_case, cadrage, or custom_folder)
   * @param contextType - Type of context (domain, use_case, cadrage, custom_folder)
   * @param contextId - Context ID (UUID)
   * @param forceReindex - Force reindexing even if already indexed
   */
  async indexContext(
    contextType: 'domain' | 'use_case' | 'cadrage' | 'custom_folder' | 'project',
    contextId: string,
    forceReindex = false
  ): Promise<IndexContextResponse> {
    const response = await apiClient.post<IndexContextResponse>(
      `${apiBaseURL}/context/${contextType}/${contextId}`,
      {},
      { params: { force_reindex: forceReindex } }
    );
    return response.data;
  },

  /**
   * Delete document chunks from Neo4j
   * @param documentId - Document ID
   */
  async deleteDocumentChunks(documentId: string): Promise<any> {
    const response = await apiClient.delete(`${apiBaseURL}/documents/${documentId}/chunks`);
    return response.data;
  },
};

export default indexingService;
